import { Static, Type } from '@sinclair/typebox'
import { PopulatedFlow } from '../flows'
import { Action } from '../flows/actions/action'
import { Connection } from '../flows/connections/connection'
import { Trigger } from '../flows/triggers/trigger'
import { AiBuilderResponseMessage } from './ai-builder-response-message'
import { AiBuilderCard } from './cards'

export * from './cards'

/**
 * Static outer net on the websocket payload — the abuse bound, not the UX
 * one. The env-driven AP_AI_BUILDER_MAX_MESSAGE_LENGTH stays the friendly
 * limit (it answers with a translated message); these caps reject at schema
 * validation what no legitimate client ever sends.
 */
export const AI_BUILDER_PROMPT_HARD_CAP = 20_000
export const AI_BUILDER_CONTEXT_HARD_CAP = 200

export const AiBuilderMessage = Type.Object({
  role: Type.Union([Type.Literal('user'), Type.Literal('assistant')]),
  content: Type.String({ maxLength: AI_BUILDER_PROMPT_HARD_CAP }),
})

export type AiBuilderMessage = Static<typeof AiBuilderMessage>

export const NavigateDirective = Type.Object({
  stepName: Type.String(),
  propToHighlight: Type.Optional(Type.String()),
  action: Type.Optional(Type.Literal('OPEN_AUTH')),
  buttonLabel: Type.Optional(Type.String()),
})

export type NavigateDirective = Static<typeof NavigateDirective>

export const SuggestedAction = Type.Object({
  label: Type.String(),
  message: Type.String(),
})

export type SuggestedAction = Static<typeof SuggestedAction>

export const AskAiBuilderResponse = Type.Object({
  /** Echo of GenerateFlowRequest.requestId */
  requestId: Type.Optional(Type.String()),
  /** Null when the turn was refused before touching the flow (busy, quota, too long, invalid payload) */
  flow: Type.Union([PopulatedFlow, Type.Null()]),
  message: Type.Optional(AiBuilderResponseMessage),
  agentStateId: Type.Optional(Type.String()),
  waitingForReply: Type.Optional(Type.Boolean()),
  navigateDirectives: Type.Optional(Type.Array(NavigateDirective)),
  suggestedActions: Type.Optional(Type.Array(SuggestedAction)),
  /** At most one interactive card per turn */
  card: Type.Optional(AiBuilderCard),
  /**
   * The wizard walked every step and the flow is complete — the same signal
   * that produced the summary and flipped the steps valid. The client used to
   * infer it from the presence of improvement suggestions, which a turn ending
   * on a skipped test never carries.
   */
  flowReady: Type.Optional(Type.Boolean()),
})

export type AskAiBuilderResponse = Static<typeof AskAiBuilderResponse>

// Mid-turn events carry their flow: the socket is one per tab, and a turn
// keeps emitting after the tab moved to another flow's chat

/**
 * One mid-turn progress line (AI_BUILDER_NARRATION event): what the turn just
 * did on its own. `content` is a Tolgee key, translated on the front.
 */
export const AiBuilderNarration = Type.Object({
  flowId: Type.String(),
  content: Type.String(),
  contentParams: Type.Optional(Type.Record(Type.String(), Type.String())),
})

export type AiBuilderNarration = Static<typeof AiBuilderNarration>

/**
 * The loader line while a tool runs (AI_BUILDER_PROGRESS event). `message` is
 * a Tolgee key, translated on the front.
 */
export const AiBuilderProgress = Type.Object({
  flowId: Type.String(),
  message: Type.String(),
})

export type AiBuilderProgress = Static<typeof AiBuilderProgress>

export const AiBuilderFlowPartialUpdate = Type.Object({
  flowId: Type.String(),
  flowElements: Type.Object({
    triggers: Type.Array(Trigger),
    actions: Type.Array(Action),
    connections: Type.Array(Connection),
  }),
})

export type AiBuilderFlowPartialUpdate = Static<
  typeof AiBuilderFlowPartialUpdate
>

export const DynamicPropertiesValues = Type.Record(Type.String(), Type.Any())

export type DynamicPropertiesValues = Static<typeof DynamicPropertiesValues>
